import { supabase } from '../lib/supabase';

class RelatoriosService {
  // Buscar todos os logs de atividade com filtros
  async buscarLogsAtividade(filtros = {}) {
    try {
      console.log('🔍 Buscando logs com filtros:', filtros);
      
      // Query direta do Supabase com joins
      let query = supabase
        .from('logs_atividade_aluno')
        .select(`
          *,
          alunos!inner(
            nome,
            email,
            escolas(
              nome
            )
          )
        `)
        .order('timestamp', { ascending: false })
        .limit(1000);
          
      // Aplicar filtros
      if (filtros.dataInicio) {
        query = query.gte('timestamp', filtros.dataInicio);
      }
      if (filtros.dataFim) {
        query = query.lte('timestamp', filtros.dataFim + ' 23:59:59');
      }
      if (filtros.alunoId) {
        query = query.eq('aluno_id', filtros.alunoId);
      }
      if (filtros.categoria) {
        query = query.eq('categoria', filtros.categoria);
      }
      if (filtros.tipoEvento) {
        query = query.eq('tipo_evento', filtros.tipoEvento);
      }
      if (filtros.escolaId) {
        query = query.eq('alunos.escola_id', filtros.escolaId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('❌ Erro na query:', error);
        throw error;
      }
      
      // Processar dados para adicionar campos calculados
      const processedData = data?.map(log => {
        const browserInfo = log.detalhes?.browser_info || {};
        const userAgent = browserInfo.userAgent || log.user_agent || '';
        
        return {
          ...log,
          aluno_nome: log.alunos?.nome || 'N/A',
          aluno_email: log.alunos?.email || 'N/A',
          escola_nome: log.alunos?.escolas?.nome || 'N/A',
          data_evento: new Date(log.timestamp).toISOString().split('T')[0],
          hora_evento: new Date(log.timestamp).getHours(),
          duracao_segundos: log.duracao_ms ? Math.round(log.duracao_ms / 1000) : null,
          materia: log.detalhes?.materia || log.detalhes?.materia_nome,
          teste_nome: log.detalhes?.teste_nome,
          view_atual: log.detalhes?.view_atual,
          questao_numero: log.detalhes?.questao_numero,
          tipo_dispositivo: this.detectarDispositivo(userAgent),
          navegador: this.detectarNavegador(userAgent)
        };
      }) || [];
      
      console.log('✅ Query executada com sucesso, registros:', processedData.length);
      return { success: true, data: processedData };
    } catch (error) {
      console.error('❌ Erro ao buscar logs:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Função auxiliar para detectar tipo de dispositivo
  detectarDispositivo(userAgent) {
    if (!userAgent) return 'N/A';
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return 'Mobile';
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return 'Tablet';
    }
    return 'Desktop';
  }
  
  // Função auxiliar para detectar navegador
  detectarNavegador(userAgent) {
    if (!userAgent) return 'N/A';
    const ua = userAgent.toLowerCase();
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('safari')) return 'Safari';
    if (ua.includes('edge')) return 'Edge';
    return 'Outro';
  }

  // Estatísticas gerais de atividade baseadas nos dados reais
  async obterEstatisticasGerais(filtros = {}) {
    try {
      console.log('📊 Calculando estatísticas gerais com filtros:', filtros);
      const { data: logs } = await this.buscarLogsAtividade(filtros);
      
      if (!logs || logs.length === 0) {
        console.log('⚠️ Nenhum log encontrado');
        return {
          success: true,
          data: {
            totalEventos: 0,
            alunosAtivos: 0,
            testesRealizados: 0,
            chatsAbertos: 0,
            eventosPorCategoria: {},
            eventosPorDia: {}
          }
        };
      }

      console.log('📈 Processando', logs.length, 'logs');
      
      // Calcular estatísticas baseadas nos dados reais
      const totalEventos = logs.length;
      const alunosUnicos = new Set(logs.map(log => log.aluno_id));
      const alunosAtivos = alunosUnicos.size;
      
      // Contar eventos específicos baseados nos tipos reais encontrados
      const testesRealizados = logs.filter(log => 
        log.tipo_evento === 'inicio_teste' || 
        log.tipo_evento === 'clique_teste'
      ).length;
      
      const chatsAbertos = logs.filter(log => 
        log.tipo_evento === 'abertura_chat' ||
        log.tipo_evento === 'clique_materia'
      ).length;

      // Eventos por categoria (baseado nos dados reais: sessao, navegacao, chat, teste, interacao, performance)
      const eventosPorCategoria = logs.reduce((acc, log) => {
        const categoria = log.categoria || 'outros';
        acc[categoria] = (acc[categoria] || 0) + 1;
        return acc;
      }, {});

      // Eventos por dia
      const eventosPorDia = logs.reduce((acc, log) => {
        const data = log.data_evento || new Date(log.timestamp).toISOString().split('T')[0];
        acc[data] = (acc[data] || 0) + 1;
        return acc;
      }, {});

      const estatisticas = {
        totalEventos,
        alunosAtivos,
        testesRealizados,
        chatsAbertos,
        eventosPorCategoria,
        eventosPorDia
      };
      
      console.log('✅ Estatísticas calculadas:', estatisticas);
      
      return {
        success: true,
        data: estatisticas
      };
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas:', error);
      return { success: false, error: error.message };
    }
  }

  // Relatório de desempenho de testes baseado nos dados reais
  async relatorioDesempenhoTestes(filtros = {}) {
    try {
      console.log('📝 Gerando relatório de testes com filtros:', filtros);
      
      const { data: logs } = await this.buscarLogsAtividade({
        ...filtros,
        categoria: 'teste'
      });

      if (!logs || logs.length === 0) {
        console.log('⚠️ Nenhum log de teste encontrado');
        return { success: true, data: [] };
      }
      
      console.log('📊 Processando', logs.length, 'logs de teste');

      // Agrupar por aluno (já que não temos teste_id nos dados reais)
      const desempenho = logs.reduce((acc, log) => {
        const alunoId = log.aluno_id;
        
        if (!acc[alunoId]) {
          acc[alunoId] = {
            aluno_nome: log.aluno_nome || 'N/A',
            escola_nome: log.escola_nome || 'N/A',
            teste_nome: log.teste_nome || 'Atividades Gerais',
            eventos: []
          };
        }
        
        acc[alunoId].eventos.push(log);
        return acc;
      }, {});

      // Processar dados de desempenho baseados nos eventos reais
      const relatorio = Object.values(desempenho).map(item => {
        const inicioTeste = item.eventos.find(e => 
          e.tipo_evento === 'inicio_teste' || 
          e.tipo_evento === 'clique_teste'
        );
        const respostas = item.eventos.filter(e => e.tipo_evento === 'resposta_questao');
        const navegacao = item.eventos.filter(e => e.tipo_evento === 'navegacao_questao');
        const mudancas = item.eventos.filter(e => e.tipo_evento === 'mudanca_resposta');

        return {
          aluno_nome: item.aluno_nome,
          escola_nome: item.escola_nome,
          teste_nome: item.teste_nome,
          data_inicio: inicioTeste?.timestamp || null,
          tempo_para_iniciar: inicioTeste?.duracao_ms || 0,
          total_respostas: respostas.length,
          total_navegacao: navegacao.length,
          total_mudancas: mudancas.length,
          taxa_mudanca: respostas.length > 0 ? (mudancas.length / respostas.length * 100).toFixed(1) : '0'
        };
      }).filter(item => item.data_inicio); // Apenas itens com início de teste

      console.log('✅ Relatório de testes gerado:', relatorio.length, 'registros');
      return { success: true, data: relatorio };
    } catch (error) {
      console.error('❌ Erro ao gerar relatório de testes:', error);
      return { success: false, error: error.message };
    }
  }

  // Relatório de uso do chat baseado nos dados reais
  async relatorioUsoChat(filtros = {}) {
    try {
      console.log('💬 Gerando relatório de chat com filtros:', filtros);
      
      const { data: logs } = await this.buscarLogsAtividade({
        ...filtros,
        categoria: 'chat'
      });

      if (!logs || logs.length === 0) {
        console.log('⚠️ Nenhum log de chat encontrado');
        return { success: true, data: [] };
      }
      
      console.log('💬 Processando', logs.length, 'logs de chat');

      // Agrupar por aluno baseado nos dados reais
      const usoChat = logs.reduce((acc, log) => {
        const alunoId = log.aluno_id;
        
        if (!acc[alunoId]) {
          acc[alunoId] = {
            aluno_nome: log.aluno_nome || 'N/A',
            escola_nome: log.escola_nome || 'N/A',
            sessoes_chat: 0,
            materias_acessadas: new Set(),
            tempo_total_chat: 0,
            eventos: []
          };
        }
        
        acc[alunoId].eventos.push(log);
        
        // Contar sessões baseado nos eventos reais
        if (log.tipo_evento === 'abertura_chat' || log.tipo_evento === 'clique_materia') {
          acc[alunoId].sessoes_chat++;
          
          // Extrair matéria dos detalhes ou do campo materia
          const materia = log.materia || log.detalhes?.materia || log.detalhes?.materia_nome;
          if (materia) {
            acc[alunoId].materias_acessadas.add(materia);
          }
        }
        
        // Somar tempo se disponível
        if (log.duracao_ms) {
          acc[alunoId].tempo_total_chat += log.duracao_ms;
        }
        
        return acc;
      }, {});

      // Processar dados finais
      const relatorio = Object.values(usoChat).map(item => {
        const materiasArray = Array.from(item.materias_acessadas);
        return {
          aluno_nome: item.aluno_nome,
          escola_nome: item.escola_nome,
          sessoes_chat: item.sessoes_chat,
          total_materias: materiasArray.length,
          tempo_total_minutos: Math.round(item.tempo_total_chat / 60000),
          tempo_medio_por_sessao: item.sessoes_chat > 0 
            ? Math.round(item.tempo_total_chat / item.sessoes_chat / 60000) 
            : 0
        };
      }).filter(item => item.sessoes_chat > 0); // Apenas alunos que usaram chat

      console.log('✅ Relatório de chat gerado:', relatorio.length, 'registros');
      return { success: true, data: relatorio };
    } catch (error) {
      console.error('❌ Erro ao gerar relatório de chat:', error);
      return { success: false, error: error.message };
    }
  }

  // Relatório de sessões baseado nos dados reais
  async relatorioSessoes(filtros = {}) {
    try {
      console.log('👥 Gerando relatório de sessões com filtros:', filtros);
      
      // Buscar todos os logs para calcular sessões (não apenas categoria sessao)
      const { data: allLogs } = await this.buscarLogsAtividade(filtros);

      if (!allLogs || allLogs.length === 0) {
        console.log('⚠️ Nenhum log encontrado para sessões');
        return { success: true, data: [] };
      }
      
      console.log('👥 Processando', allLogs.length, 'logs para sessões');

      // Agrupar por sessão_id baseado nos dados reais
      const sessoes = allLogs.reduce((acc, log) => {
        const sessaoId = log.sessao_id;
        if (!sessaoId) return acc;
        
        if (!acc[sessaoId]) {
          acc[sessaoId] = {
            sessao_id: sessaoId,
            aluno_nome: log.aluno_nome || 'N/A',
            escola_nome: log.escola_nome || 'N/A',
            eventos: []
          };
        }
        
        acc[sessaoId].eventos.push(log);
        return acc;
      }, {});

      // Processar dados das sessões baseado nos eventos reais
      const relatorio = Object.values(sessoes).map(sessao => {
        const eventos = sessao.eventos.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        const inicio = eventos.find(e => e.tipo_evento === 'sessao_inicio');
        const fim = eventos.find(e => e.tipo_evento === 'logout_aluno');
        
        const dataInicio = inicio?.timestamp || eventos[0]?.timestamp;
        const dataFim = fim?.timestamp || eventos[eventos.length - 1]?.timestamp;
        
        let duracaoMinutos = 0;
        if (dataInicio && dataFim && dataInicio !== dataFim) {
          duracaoMinutos = Math.round((new Date(dataFim) - new Date(dataInicio)) / 60000);
        }
        
        // Extrair informações do dispositivo dos detalhes
        const browserInfo = inicio?.detalhes?.browser_info || eventos[0]?.detalhes?.browser_info || {};
        const dispositivo = browserInfo.platform || sessao.tipo_dispositivo || 'N/A';

        return {
          aluno_nome: sessao.aluno_nome,
          escola_nome: sessao.escola_nome,
          data_inicio: dataInicio,
          duracao_minutos: duracaoMinutos,
          total_eventos: eventos.length,
          dispositivo: dispositivo
        };
      }).filter(sessao => sessao.total_eventos > 1); // Apenas sessões com mais de 1 evento

      console.log('✅ Relatório de sessões gerado:', relatorio.length, 'registros');
      return { success: true, data: relatorio };
    } catch (error) {
      console.error('❌ Erro ao gerar relatório de sessões:', error);
      return { success: false, error: error.message };
    }
  }

  // Buscar lista de alunos para filtros
  async buscarAlunos(escolaId = null) {
    try {
      console.log('📋 Buscando alunos para filtros');
      
      let query = supabase
        .from('alunos')
        .select('id, nome, email, escola_id, escolas(nome)')
        .order('nome');

      if (escolaId) {
        query = query.eq('escola_id', escolaId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      console.log('✅ Alunos encontrados:', data?.length || 0);
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erro ao buscar alunos:', error);
      return { success: false, error: error.message };
    }
  }

  // Buscar lista de escolas para filtros
  async buscarEscolas() {
    try {
      console.log('🏫 Buscando escolas para filtros');
      
      const { data, error } = await supabase
        .from('escolas')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      
      console.log('✅ Escolas encontradas:', data?.length || 0);
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erro ao buscar escolas:', error);
      return { success: false, error: error.message };
    }
  }

  // Relatório de logs de acesso baseado nos dados reais
  async relatorioLogsAcesso(filtros = {}) {
    try {
      console.log('🔐 Gerando relatório de logs de acesso com filtros:', filtros);
      
      // Buscar todos os logs para análise completa de acesso
      const { data: allLogs } = await this.buscarLogsAtividade(filtros);

      if (!allLogs || allLogs.length === 0) {
        console.log('⚠️ Nenhum log encontrado para acesso');
        return { success: true, data: [] };
      }
      
      console.log('🔐 Processando', allLogs.length, 'logs para acesso');

      // Filtrar eventos de acesso baseados nos tipos reais encontrados
      const logsAcesso = allLogs.filter(log => 
        log.tipo_evento === 'sessao_inicio' || 
        log.tipo_evento === 'logout_aluno' ||
        log.tipo_evento === 'acesso_dashboard' ||
        log.tipo_evento === 'carregamento_pagina'
      );
      
      console.log('🔐 Logs de acesso filtrados:', logsAcesso.length);

      // Agrupar por aluno e data
      const acessos = logsAcesso.reduce((acc, log) => {
        const data = log.data_evento || new Date(log.timestamp).toISOString().split('T')[0];
        const key = `${log.aluno_id}_${data}`;
        
        if (!acc[key]) {
          acc[key] = {
            aluno_nome: log.aluno_nome || 'N/A',
            escola_nome: log.escola_nome || 'N/A',
            data: data,
            primeiro_acesso: null,
            ultimo_acesso: null,
            total_logins: 0,
            sessoes: new Set(),
            tempo_total_sessao: 0,
            ip_address: log.ip_address || 'N/A',
            dispositivo: log.tipo_dispositivo || 'N/A',
            eventos: []
          };
        }
        
        acc[key].eventos.push(log);
        
        // Contar sessões únicas
        if (log.sessao_id) {
          acc[key].sessoes.add(log.sessao_id);
        }
        
        // Atualizar primeiro e último acesso
        if (!acc[key].primeiro_acesso || log.timestamp < acc[key].primeiro_acesso) {
          acc[key].primeiro_acesso = log.timestamp;
        }
        if (!acc[key].ultimo_acesso || log.timestamp > acc[key].ultimo_acesso) {
          acc[key].ultimo_acesso = log.timestamp;
        }
        
        // Contar logins
        if (log.tipo_evento === 'sessao_inicio' || log.tipo_evento === 'acesso_dashboard') {
          acc[key].total_logins++;
        }
        
        // Somar duração se disponível
        if (log.duracao_ms) {
          acc[key].tempo_total_sessao += log.duracao_ms;
        }
        
        return acc;
      }, {});

      // Processar dados finais
      const relatorio = Object.values(acessos).map(item => {
        const tempoTotalMinutos = Math.round(item.tempo_total_sessao / 60000);
        const totalSessoes = item.sessoes.size;
        
        return {
          aluno_nome: item.aluno_nome,
          escola_nome: item.escola_nome,
          data_formatada: new Date(item.data).toLocaleDateString('pt-BR'),
          primeiro_acesso_formatado: item.primeiro_acesso 
            ? new Date(item.primeiro_acesso).toLocaleTimeString('pt-BR')
            : 'N/A',
          ultimo_acesso_formatado: item.ultimo_acesso 
            ? new Date(item.ultimo_acesso).toLocaleTimeString('pt-BR')
            : 'N/A',
          total_logins: totalSessoes, // Usar número de sessões como proxy para logins
          tempo_total_minutos: tempoTotalMinutos,
          status_sessao: item.eventos.some(e => e.tipo_evento === 'logout_aluno') ? 'Finalizada' : 'Ativa',
          dispositivo: item.dispositivo,
          ip_address: item.ip_address
        };
      }).sort((a, b) => new Date(b.data_formatada.split('/').reverse().join('-')) - new Date(a.data_formatada.split('/').reverse().join('-')));

      console.log('✅ Relatório de logs de acesso gerado:', relatorio.length, 'registros');
      return { success: true, data: relatorio };
    } catch (error) {
      console.error('❌ Erro ao gerar relatório de logs de acesso:', error);
      return { success: false, error: error.message };
    }
  }

  // Exportar dados para CSV
  exportarCSV(dados, nomeArquivo) {
    if (!dados || dados.length === 0) {
      alert('Não há dados para exportar');
      return;
    }

    const headers = Object.keys(dados[0]);
    const csvContent = [
      headers.join(','),
      ...dados.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'object' && value !== null) {
            return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${nomeArquivo}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export default new RelatoriosService();
